import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";
import { Loader2, Medal, UserMinus } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { cn } from "src/lib/utils";
import { Switch } from "src/components/ui/switch";
import { Label as UILabel } from "src/components/ui/label";
import { Alert, AlertDescription } from "src/components/ui/alert";

const RankingChart = ({ evaluationData, loading = false, error = null }) => {
  const [viewMode, setViewMode] = useState("average"); // 'average' or 'sum'
  const [excludeSelfAssessment, setExcludeSelfAssessment] = useState(false);

  // Process data from the parent component
  const rankingData = useMemo(() => {
    // Return early if no data available
    if (
      !evaluationData ||
      !evaluationData.solutions ||
      evaluationData.solutions.length === 0
    ) {
      return [];
    }

    return evaluationData.solutions
      .filter((solution) => {
        // Show AI only if self-assessment is EXCLUDED (checkbox is checked)
        if (solution.user_id === null && !excludeSelfAssessment) return false;
        if (solution.user_id === null && excludeSelfAssessment) return true;
        return true;
      })
      .map((solution) => {
        // Filter out self-evaluations if needed
        const evaluations = excludeSelfAssessment
          ? solution.evaluations.filter(
              (e) =>
                e.evaluator_id !== solution.user_id &&
                !(solution.user_id === null && e.evaluator_id === null)
            )
          : solution.evaluations;

        const scores = evaluations.map((e) => e.score);
        const totalScore = scores.reduce((a, b) => a + b, 0);
        const averageScore = scores.length > 0 ? totalScore / scores.length : 0;

        return {
          name:
            solution.user_id === null
              ? "AI Riešenie"
              : solution.users
              ? `${solution.users.first_name} ${solution.users.last_name}`
              : "Unknown",
          averageScore: parseFloat(averageScore.toFixed(2)),
          totalScore: totalScore,
          totalEvaluations: scores.length,
        };
      })
      .sort(
        (a, b) =>
          b[viewMode === "average" ? "averageScore" : "totalScore"] -
          a[viewMode === "average" ? "averageScore" : "totalScore"]
      );
  }, [evaluationData, excludeSelfAssessment, viewMode]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { totalScore, averageScore, totalEvaluations } = payload[0].payload;
      return (
        <div className="p-3 bg-white rounded-lg border shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-gray-600">
            Priemer: {averageScore.toFixed(2)}
          </p>
          <p className="text-sm text-gray-600">Súčet: {totalScore}</p>
          <p className="text-sm text-gray-600">
            Počet hodnotení: {totalEvaluations}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Handle the case when data is empty
  if (rankingData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center space-x-2">
            <Medal className="w-5 h-5 text-yellow-500" />
            <CardTitle>Rebríček hodnotení</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
            <p className="text-gray-500">
              Žiadne dáta o hodnoteniach nie sú dostupné
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center space-x-2">
          <Medal className="w-5 h-5 text-yellow-500" />
          <CardTitle>Rebríček hodnotení</CardTitle>
        </div>

        {/* Controls row with filter and view mode selector */}
        <div className="flex flex-wrap gap-3 justify-between items-center mt-3">
          {/* Self-assessment filter */}
          <div className="flex items-center p-1.5 px-3 space-x-2 bg-gray-50 rounded-full">
            <Switch
              id="exclude-self-assessment-ranking"
              checked={excludeSelfAssessment}
              onCheckedChange={setExcludeSelfAssessment}
              className="data-[state=checked]:bg-blue-600"
            />
            <UILabel
              htmlFor="exclude-self-assessment-ranking"
              className="flex items-center text-sm font-medium cursor-pointer"
            >
              <UserMinus className="mr-1.5 w-3.5 h-3.5" />
              Bez sebahodnotenia
            </UILabel>
          </div>

          {/* View mode selector */}
          <div className="flex p-1 space-x-1 bg-gray-100 rounded-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("average")}
              className={cn(
                "rounded-md text-sm h-8",
                viewMode === "average" && "bg-white shadow-sm"
              )}
            >
              Priemer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("sum")}
              className={cn(
                "rounded-md text-sm h-8",
                viewMode === "sum" && "bg-white shadow-sm"
              )}
            >
              Súčet
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rankingData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={viewMode === "average" ? [0, 5] : [0, "dataMax"]}
              >
                <Label
                  value={
                    viewMode === "average"
                      ? "Hodnotenie (0-5)"
                      : "Celkové skóre"
                  }
                  position="bottom"
                  offset={15}
                  style={{
                    fill: "#6b7280",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </XAxis>
              <YAxis
                dataKey="name"
                type="category"
                width={100}
                tickFormatter={(value) => {
                  return value.length > 15
                    ? value.substring(0, 15) + "..."
                    : value;
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey={viewMode === "average" ? "averageScore" : "totalScore"}
                fill="#3b82f6"
                radius={[0, 4, 4, 0]}
                label={{
                  position: "right",
                  formatter: (value) =>
                    viewMode === "average" ? value.toFixed(2) : value,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default RankingChart;
